import React, { useMemo } from 'react';
import { useEEGAnalysis, useConnectionState } from '../../../stores/processedDataStore';
import { useSensorContactStatus } from '../../../stores/systemStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { getIndexGuide } from '../../../constants/indexGuides';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from "../../../locales";
import { useLanguageStore } from "../../../stores/languageStore";

const EEGBandPowerCards: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const eegAnalysis = useEEGAnalysis();
  const isConnected = useConnectionState();
  const { isSensorContacted, leadOffStatus } = useSensorContactStatus();

  // 주파수 밴드 정의
  const frequencyBands = {
    delta: { min: 1, max: 4, name: t.visualizer.deltaBand, color: 'bg-amber-600' },
    theta: { min: 4, max: 8, name: t.visualizer.thetaBand, color: 'bg-orange-500' },
    alpha: { min: 8, max: 13, name: t.visualizer.alphaBand, color: 'bg-green-500/10' },
    beta: { min: 13, max: 30, name: t.visualizer.betaBand, color: 'bg-indigo-500' },
    gamma: { min: 30, max: 45, name: t.visualizer.gammaBand, color: 'bg-purple-500' }
  };

  // Power Spectrum 데이터에서 주파수 밴드별 파워 계산
  const bandPowers = useMemo(() => {
    if (!eegAnalysis?.frequencySpectrum?.frequencies || 
        !eegAnalysis?.frequencySpectrum?.ch1Power || 
        !eegAnalysis?.frequencySpectrum?.ch2Power) {
      return null;
    }

    const { frequencies, ch1Power, ch2Power } = eegAnalysis.frequencySpectrum;
    
    // 안전한 데이터 검증
    if (!Array.isArray(frequencies) || !Array.isArray(ch1Power) || !Array.isArray(ch2Power) || 
        frequencies.length !== ch1Power.length || frequencies.length !== ch2Power.length) {
      return null;
    }



    const results: Record<string, { ch1: number; ch2: number; combined: number }> = {};

    // 각 주파수 밴드별로 파워 합산
    Object.entries(frequencyBands).forEach(([bandName, band]) => {
      let ch1Sum = 0;
      let ch2Sum = 0;
      let count = 0;

      frequencies.forEach((freq, index) => {
        if (freq >= band.min && freq <= band.max) {
          ch1Sum += ch1Power[index];
          ch2Sum += ch2Power[index];
          count++;
        }
      });

      // 평균 파워 계산 (dB 단위)
      const ch1Avg = count > 0 ? ch1Sum / count : 0;
      const ch2Avg = count > 0 ? ch2Sum / count : 0;
      const combined = (ch1Avg + ch2Avg) / 2;

      results[bandName] = {
        ch1: ch1Avg,
        ch2: ch2Avg,
        combined: combined
      };
    });


    return results;
  }, [eegAnalysis?.frequencySpectrum]);

  // 카드 데이터 생성
  const cardData = useMemo(() => {
    if (!bandPowers) return [];

    // 전체 채널별 최대값 계산 (정규화용)
    const allCh1Powers = Object.values(bandPowers).map(p => p.ch1);
    const allCh2Powers = Object.values(bandPowers).map(p => p.ch2);
    const maxCh1Power = Math.max(...allCh1Powers);
    const maxCh2Power = Math.max(...allCh2Powers);
    const maxOverallPower = Math.max(maxCh1Power, maxCh2Power);

    return Object.entries(frequencyBands).map(([bandKey, band]) => {
      const power = bandPowers[bandKey];
      const normalizedCh1 = maxOverallPower > 0 ? (power.ch1 / maxOverallPower) * 100 : 0;
      const normalizedCh2 = maxOverallPower > 0 ? (power.ch2 / maxOverallPower) * 100 : 0;
      const normalizedCombined = maxOverallPower > 0 ? (power.combined / maxOverallPower) * 100 : 0;

      return {
        label: band.name,
        value: power.combined,
        normalizedValue: normalizedCombined,
        unit: 'dB',
        color: band.color,
        description: `${band.min}-${band.max}Hz`,
        ch1Power: power.ch1,
        ch2Power: power.ch2,
        normalizedCh1,
        normalizedCh2,
        frequencyRange: `${band.min}-${band.max}Hz`
      };
    });
  }, [bandPowers]);

  // 데이터 품질 상태 계산
  const dataQuality = useMemo(() => {
    const sensorOk = isSensorContacted;
    const bothChannelsOk = !leadOffStatus.fp1 && !leadOffStatus.fp2;
    const hasSpectrumData = !!eegAnalysis?.frequencySpectrum?.frequencies;
    const overall = sensorOk && bothChannelsOk && hasSpectrumData;
    
    return {
      sensorContact: sensorOk,
      channelQuality: bothChannelsOk,
      spectrumData: hasSpectrumData,
      overall: overall
    };
  }, [isSensorContacted, leadOffStatus, eegAnalysis?.frequencySpectrum]);

  // 연결되지 않았거나 데이터가 없는 경우
  if (!isConnected || !bandPowers) {
    return (
      <div className="w-full space-y-6">
        <div className="text-center py-8">
          
          <div className="text-base text-foreground">
            {t.visualizer.noGraphData}
          </div>
          <div className="text-sm text-muted-foreground">
            {t.visualizer.connectDevice}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t.visualizer.bandPowerConnect}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full space-y-6 mt-10 mb-10">
      {/* 센서 접촉 상태 경고 */}
      {isConnected && !isSensorContacted && (
        <div className="mb-3 p-2 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <div className="text-red-300">
            <div className="font-medium text-sm">{t.visualizer.poorContactDetection}</div>
            <div className="text-xs">
              FP1: {leadOffStatus.fp1 ? t.visualizer.contactPoor : t.visualizer.contactNormal}, 
              FP2: {leadOffStatus.fp2 ? t.visualizer.contactPoor : t.visualizer.contactNormal} - 
              {t.visualizer.bandPowerAccuracyDegrade}
            </div>
          </div>
        </div>
      )}

      {/* 주파수 밴드 파워 카드들 */}
      <TooltipProvider>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {cardData.map((card, index) => (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <div className="bg-card border border-border rounded-lg p-3 hover:bg-card/70 transition-colors cursor-help">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${card.color}`}></div>
                    <span className="text-xs text-muted-foreground">{card.unit}</span>
                  </div>
                  
                  {/* 전체 파워 막대그래프 */}
                  <div className="mb-2">
                    <div className="text-xs text-muted-foreground mb-1">{t.common.all}</div>
                    <div className="h-8 bg-muted rounded-md relative overflow-hidden">
                      <div 
                        className={`absolute bottom-0 left-0 right-0 ${card.color} opacity-80 rounded-md transition-all duration-300`}
                        style={{ height: `${card.normalizedValue}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-white drop-shadow-lg">
                          {card.value.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 채널별 막대그래프 */}
                  <div className="space-y-1 mb-2">
                    {/* Ch1 (좌뇌) */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-indigo-400">Ch1</span>
                        <span className="text-xs text-muted-foreground">{card.ch1Power.toFixed(1)}</span>
                      </div>
                      <div className="h-6 bg-muted rounded-sm relative overflow-hidden">
                        <div 
                          className="absolute bottom-0 left-0 right-0 bg-indigo-500 opacity-80 rounded-sm transition-all duration-300"
                          style={{ height: `${card.normalizedCh1}%` }}
                        />
                      </div>
                    </div>

                    {/* Ch2 (우뇌) */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-red-300">Ch2</span>
                        <span className="text-xs text-muted-foreground">{card.ch2Power.toFixed(1)}</span>
                      </div>
                      <div className="h-6 bg-muted rounded-sm relative overflow-hidden">
                        <div 
                          className="absolute bottom-0 left-0 right-0 bg-red-500 opacity-80 rounded-sm transition-all duration-300"
                          style={{ height: `${card.normalizedCh2}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-foreground">
                      {card.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {card.frequencyRange}
                    </div>
                    
                    {/* 좌우뇌 차이 표시 */}
                    <div className="text-xs text-muted-foreground mt-1">
                      <span className="text-cyan-300">
                        {t.visualizer.bandPowerDiff}: {Math.abs(card.ch1Power - card.ch2Power).toFixed(1)} dB
                      </span>
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-slate-900 border border-slate-700 shadow-xl">
                <div className="max-w-lg p-4 bg-card rounded-lg">
                  <div 
                    className="text-sm leading-relaxed space-y-2 [&>strong]:text-indigo-400 [&>strong]:font-semibold [&>br]:block [&>br]:mb-1"
                    style={{
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                    }}
                  >
                    <div className="font-semibold text-indigo-400 mb-2">{card.label} ({card.frequencyRange})</div>
                    <div className="text-cyan-300">
                      <strong>{t.visualizer.tooltipDescription}:</strong> {card.description}
                    </div>
                    <div className="text-green-300">
                      <strong>{t.visualizer.tooltipTotalPower}:</strong> {card.value.toFixed(2)} dB
                    </div>
                    <div className="text-yellow-300">
                      <strong>{t.visualizer.tooltipChannelPower}:</strong><br/>
                      • Ch1 (FP1/좌뇌): {card.ch1Power.toFixed(2)} dB<br/>
                      • Ch2 (FP2/우뇌): {card.ch2Power.toFixed(2)} dB
                    </div>
                    <div className="text-purple-300">
                      <strong>{t.visualizer.tooltipBrainDiff}:</strong> {Math.abs(card.ch1Power - card.ch2Power).toFixed(2)} dB<br/>
                      <span className="text-xs">
                        {card.ch1Power > card.ch2Power ? t.visualizer.leftBrainDominant : 
                         card.ch2Power > card.ch1Power ? t.visualizer.rightBrainDominant : t.visualizer.balancedState}
                      </span>
                    </div>
                    <div className="text-orange-300">
                      <strong>{t.visualizer.tooltipInterpretation}:</strong> {t.visualizer.tooltipBandInterpretation.replace('{range}', card.frequencyRange)}
                    </div>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>

      {/* 데이터 상태 정보 - 여백 줄임 */}
      <div className="text-xs text-muted-foreground text-center mt-2">
        {t.visualizer.bandPowerLabel} • 
        {t.visualizer.dataQualityLabel}: {dataQuality.overall ? t.visualizer.contactNormal : t.visualizer.contactPoor} • 
        {t.visualizer.spectrumLabel}: {dataQuality.spectrumData ? 'Receiving' : 'Waiting'}
      </div>
    </div>
  );
};

export default EEGBandPowerCards; 